import type { Id } from "@/packages/types";
import i18n from "i18next";

/**
 * Auth User type
 * Represents the authenticated user data
 */
export interface AuthUser {
  _id: Id<"users">;
  _creationTime: number;
  email?: string;
  name?: string;
  emailVerificationTime?: number;
  phone?: string;
  phoneVerificationTime?: number;
  isAnonymous?: boolean;
}

/**
 * Auth state interface
 * Represents the current authentication state
 */
export interface AuthState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;
  /** The current user or null if not authenticated */
  user: AuthUser | null;
  /** Whether the auth state is loading */
  isLoading: boolean;
  /** Optional error message */
  error?: string | null;
}

/**
 * Sign-in credentials
 */
export interface SignInCredentials {
  email: string;
  password: string;
}

/**
 * Sign-up credentials
 */
export interface SignUpCredentials {
  email: string;
  password: string;
  confirmPassword: string;
  name?: string;
}

/**
 * Password requirement interface
 * Used for password validation UI
 */
export interface PasswordRequirement {
  /** Label describing the requirement */
  label: string;
  /** Whether the requirement is met */
  isValid: boolean;
}

/**
 * Get default password requirement labels with i18n support.
 * Returns translated labels at call time so language changes are reflected.
 * Used when custom labels are not provided.
 */
export const getDefaultPasswordLabels = () => ({
  minLength: i18n.t("auth:passwordRequirements.minLength"),
  uppercase: i18n.t("auth:passwordRequirements.uppercase"),
  lowercase: i18n.t("auth:passwordRequirements.lowercase"),
  number: i18n.t("auth:passwordRequirements.number"),
});

/**
 * Auth error types
 */
export interface AuthError {
  code: string;
  message: string;
  retryAfter?: number;
}

/**
 * Sign-in result
 */
export interface SignInResult {
  success: boolean;
  error?: AuthError;
}

/**
 * Sign-up result
 */
export interface SignUpResult {
  success: boolean;
  error?: AuthError;
}

/**
 * Auth step during sign-up flow
 */
export type AuthStep = "credentials" | "otp-verification";

/**
 * OTP verification credentials
 */
export interface VerifyOtpCredentials {
  email: string;
  code: string;
}
